import { createTheme as createMuiTheme, Theme, ThemeOptions } from '@mui/material';
import merge from 'deepmerge';
import _ from 'lodash';
import { DASHBOARD_BACKGROUND_COLORS, RESQ_BRAND_COLORS } from 'src/constants/palette';

export type ThemeMode = 'light' | 'dark';

type ColorRole = 'primary' | 'secondary' | 'critical' | 'success';

/**
 * Menus, select lists, popovers and search dropdowns float above the page, so unlike
 * resting surfaces they keep a border and shadow to show depth.
 */
export const getFloatingSurfaceStyles = (theme: Theme) => ({
    backgroundColor: theme.resqPalette.surface.default,
    color: theme.resqPalette.text.default,
    border: `1px solid ${theme.resqPalette.border.subdued}`,
    // A string, not a number: `sx` multiplies a bare number by theme.shape.borderRadius (8 -> 32px).
    borderRadius: '8px',
    boxShadow:
        theme.palette.mode === 'dark'
            ? '0px 8px 24px rgba(0, 0, 0, 0.56), 0px 2px 6px rgba(0, 0, 0, 0.4)'
            : '0px 8px 24px rgba(16, 24, 40, 0.12), 0px 2px 6px rgba(16, 24, 40, 0.08)',
});

/** Paper nested inside a floating surface (e.g. an inline Autocomplete list); the parent draws the edge. */
export const embeddedSurfaceStyles = {
    margin: 0,
    border: 'none',
    borderRadius: 0,
    boxShadow: 'none',
} as const;

const DEFAULT_TEXT_VARIANTS = ['h1', 'h2', 'h3', 'h4', 'h5', 'body1', 'body2'] as const;
const SUBDUED_TEXT_VARIANTS = ['subtitle1', 'subtitle2', 'caption', 'body3'] as const;

/**
 * Base typography variants bake in the light navy and slate text colors. MUI parts that spread a
 * variant (such as the pagination labels) use that color directly, so each theme recolors them.
 */
export const withTypographyTextColors = (
    typography: Theme['typography'],
    colors: { default: string; subdued: string },
) => {
    const recolor = (variants: readonly (keyof Theme['typography'])[], color: string) =>
        Object.fromEntries(
            variants.map((variant) => [variant, { ...(typography[variant] as object), color }]),
        );

    return {
        ...typography,
        ...recolor(DEFAULT_TEXT_VARIANTS, colors.default),
        ...recolor(SUBDUED_TEXT_VARIANTS, colors.subdued),
    };
};

type ThemeComponents = NonNullable<Theme['components']>;
type ComponentOverride = {
    defaultProps?: object;
    styleOverrides?: Record<string, unknown>;
};

const resolveStyleOverride = (style: unknown, props: object) =>
    (typeof style === 'function' ? style(props) : style) as object;

/**
 * Layers a dashboard's component overrides on top of the base theme's instead of replacing them,
 * so base defaultProps and style slots stay in place. When both set the same slot, the two are
 * resolved against the rendering theme and deep merged, and the dashboard's values win.
 */
export const mergeComponentOverrides = (
    base: ThemeComponents | undefined,
    overrides: ThemeComponents,
): ThemeComponents => {
    const merged = { ...base } as Record<string, ComponentOverride>;

    for (const [name, override] of Object.entries(overrides) as [string, ComponentOverride][]) {
        const baseOverride = merged[name] ?? {};
        const styleOverrides = { ...baseOverride.styleOverrides };

        for (const [slot, style] of Object.entries(override.styleOverrides ?? {})) {
            const baseStyle = styleOverrides[slot];
            if (baseStyle === undefined) {
                styleOverrides[slot] = style;
            } else if (typeof baseStyle !== 'function' && typeof style !== 'function') {
                styleOverrides[slot] = merge(baseStyle as object, style as object);
            } else {
                styleOverrides[slot] = (props: object) =>
                    merge(
                        resolveStyleOverride(baseStyle, props),
                        resolveStyleOverride(style, props),
                    );
            }
        }

        merged[name] = {
            ...baseOverride,
            ...override,
            defaultProps: { ...baseOverride.defaultProps, ...override.defaultProps },
            styleOverrides,
        };
    }

    return merged as ThemeComponents;
};

export const getActionPalette = (mode: ThemeMode = 'light') => {
    const isDark = mode === 'dark';
    const primarySurface = isDark ? '#C44D0D' : '#365F7D';
    const primaryPressed = isDark ? '#A63C08' : '#24455E';
    const primaryText = isDark ? '#78B7F0' : '#2463A6';
    const primaryTextPressed = isDark ? '#C0D7E8' : '#24455E';
    const primaryTextDisabled = isDark ? '#6E8195' : '#909097';
    const primaryBorder = primaryText;
    const primaryBorderPressed = primaryTextPressed;
    const primaryBorderDisabled = isDark ? '#694633' : '#A9BBC4';
    // Keep disabled actions subdued against each mode’s surface.
    const primaryDisabled = primaryBorderDisabled;
    const secondarySurface = isDark ? '#24464D' : '#EEF0F2';
    const secondaryHovered = isDark ? '#30545B' : '#D6D8DC';
    const secondaryPressed = isDark ? '#3A6068' : '#D6D8DC';
    const secondaryDisabled = isDark ? '#1C3B41' : '#F2F4F6';
    const secondaryBorder = isDark ? '#718D92' : '#EEF0F2';
    const secondaryBorderPressed = isDark ? '#ABC1C4' : '#D6D8DC';
    const secondaryBorderDisabled = isDark ? '#30545B' : '#F2F4F6';
    const secondaryOn = isDark ? RESQ_BRAND_COLORS.onDark : RESQ_BRAND_COLORS.deepTeal;

    return {
        primary: {
            mui: {
                // Filled actions retain white labels; text actions use a separate blue family.
                main: primarySurface,
                dark: primaryPressed,
                contrastText: '#FFFFFF',
            },
            surface: primarySurface,
            hovered: isDark ? '#B84409' : '#2D526E',
            pressed: primaryPressed,
            disabled: primaryDisabled,
            subduedHovered: isDark ? '#14354C' : '#EDF4FB',
            subduedPressed: isDark ? '#2A4962' : '#DFEAF2',
            text: primaryText,
            textPressed: primaryTextPressed,
            textDisabled: primaryTextDisabled,
            onText: '#FFFFFF',
            icon: primaryText,
            iconDisabled: primaryTextDisabled,
            border: primaryBorder,
            borderPressed: primaryBorderPressed,
            borderDisabled: primaryBorderDisabled,
        },
        secondary: {
            mui: {
                main: secondarySurface,
                dark: secondaryPressed,
                contrastText: secondaryOn,
            },
            surface: secondarySurface,
            hovered: secondaryHovered,
            pressed: secondaryPressed,
            disabled: secondaryDisabled,
            text: secondaryOn,
            onText: secondaryOn,
            border: secondaryBorder,
            borderPressed: secondaryBorderPressed,
            borderDisabled: secondaryBorderDisabled,
        },
    };
};

/** Navigation selection follows the shared blue interaction colors. */
export const getNavigationPalette = (mode: ThemeMode = 'light') => {
    const { primary } = getActionPalette(mode);
    return { text: primary.text, selected: primary.subduedHovered };
};

/** Semantic states are shared by every dashboard; filled actions keep white labels. */
export const getSemanticPalette = (mode: ThemeMode = 'light') => {
    const isDark = mode === 'dark';
    const red = isDark ? '#FF8294' : '#C33449';
    const green = isDark ? '#65D6A0' : '#237A52';
    const yellow = isDark ? '#F2CA5C' : '#80600B';
    const redTint = isDark ? '#442831' : '#FDEEF0';
    const greenTint = isDark ? '#153D30' : '#EAF5EE';
    const yellowTint = isDark ? '#3F351D' : '#FFF7DB';
    const disabled = isDark ? '#A3B0BF' : '#626B76';
    return {
        surface: {
            critical: '#C33449',
            criticalHovered: '#A82B3D',
            criticalPressed: '#8D2333',
            criticalDisabled: disabled,
            criticalSubdued: redTint,
            criticalSubduedHovered: redTint,
            criticalSubduedPressed: isDark ? '#59303C' : '#F8DDE2',
            success: '#237A52',
            successSecondary: green,
            successHovered: '#1C6343',
            successPressed: '#164E35',
            successDisabled: disabled,
            successSubdued: greenTint,
            successSubduedHovered: greenTint,
            successSubduedPressed: isDark ? '#1C4B3B' : '#D8EDDF',
            warning: isDark ? '#F2CA5C' : '#E6B52E',
            warningSecondary: isDark ? '#F2CA5C' : '#E6B52E',
            warningSubdued: yellowTint,
            warningSubduedAlt: yellowTint,
        },
        text: {
            critical: red,
            criticalPressed: isDark ? '#FFA0AD' : '#8D2333',
            criticalDisabled: disabled,
            onCritical: '#FFFFFF',
            onCriticalSubdued: red,
            success: green,
            successSecondary: green,
            successPressed: isDark ? '#8CE2B8' : '#164E35',
            successDisabled: disabled,
            onSuccess: '#FFFFFF',
            onSuccessSubdued: green,
            warning: yellow,
            onWarning: '#0E262A',
            onWarningSubdued: yellow,
            onWarningSubduedAlt: yellow,
        },
        icon: {
            critical: red,
            criticalDisabled: disabled,
            onCritical: '#FFFFFF',
            success: green,
            successDisabled: disabled,
            onSuccess: '#FFFFFF',
            warning: yellow,
            onWarning: '#0E262A',
            interactiveCritical: red,
            interactiveCriticalHovered: red,
            interactiveCriticalPressed: red,
            interactiveCriticalDisabled: disabled,
        },
        border: {
            critical: red,
            criticalPressed: red,
            criticalDisabled: disabled,
            criticalSubdued: red,
            onCritical: 'rgba(15, 16, 26, 0.3)',
            success: green,
            successPressed: green,
            successDisabled: disabled,
            successSubdued: green,
            onSuccess: 'rgba(15, 16, 26, 0.3)',
            warning: yellow,
            warningSubdued: yellow,
            warningSubduedAlt: yellow,
        },
    };
};

/** Shared app surfaces and controls; dashboards only add their domain-specific colors. */
export const getInterfacePalette = (mode: ThemeMode = 'light') => {
    const isDark = mode === 'dark';
    const semantic = getSemanticPalette(mode);
    const { primary, secondary } = getActionPalette(mode);
    const surface = isDark ? '#163238' : '#FFFFFF';
    const subdued = isDark ? '#1C3B41' : '#F2F4F6';
    const text = isDark ? RESQ_BRAND_COLORS.onDark : RESQ_BRAND_COLORS.deepTeal;
    const textSubdued = isDark ? '#ABC1C4' : RESQ_BRAND_COLORS.slate;
    const disabled = isDark ? '#A3B0BF' : '#626B76';
    const border = isDark ? '#718D92' : '#838B95';
    const borderSubdued = isDark ? '#30545B' : '#D6D8DC';

    return {
        toggle: {
            track: isDark ? border : '#A3ABB4',
            trackHovered: isDark ? '#839DA2' : '#929CA7',
            trackBorder: border,
            checkedTrack: isDark ? primary.text : primary.surface,
            checkedTrackHovered: isDark ? primary.textPressed : primary.hovered,
            thumb: isDark ? RESQ_BRAND_COLORS.deepTeal : RESQ_BRAND_COLORS.white,
        },
        background: {
            default: DASHBOARD_BACKGROUND_COLORS[mode],
            navigation: isDark ? surface : DASHBOARD_BACKGROUND_COLORS.light,
        },
        surface: {
            ...semantic.surface,
            disabled: isDark ? '#223246' : '#EEF0F2',
            default: surface,
            subdued,
            hovered: isDark ? '#24464D' : subdued,
            pressed: primary.subduedPressed,
            selected: primary.subduedHovered,
            tableSubdued: isDark ? surface : subdued,
            primary: primary.surface,
            primaryHovered: primary.hovered,
            primaryPressed: primary.pressed,
            primaryDisabled: primary.disabled,
            primarySubduedHovered: primary.subduedHovered,
            primarySubduedPressed: primary.subduedPressed,
            secondary: secondary.surface,
            secondaryHovered: secondary.hovered,
            secondaryPressed: secondary.pressed,
            secondaryDisabled: secondary.disabled,
            interactive: primary.surface,
            interactiveDisabled: primary.disabled,
        },
        text: {
            ...semantic.text,
            default: text,
            subdued: textSubdued,
            disabled,
            primary: primary.text,
            primaryPressed: primary.textPressed,
            primaryDisabled: primary.textDisabled,
            onPrimary: primary.onText,
            onSecondary: secondary.onText,
            interactive: primary.text,
            interactiveHovered: primary.textPressed,
            interactivePressed: primary.textPressed,
            interactiveDisabled: disabled,
            link: primary.text,
            placeHolder: isDark ? '#9AAEBF' : '#6F6F77',
        },
        icon: {
            ...semantic.icon,
            default: text,
            subdued: textSubdued,
            disabled,
            primary: primary.icon,
            onPrimary: primary.onText,
            tertiary: primary.icon,
            tertiaryDisabled: primary.iconDisabled,
            interactive: primary.icon,
            interactiveHovered: primary.textPressed,
            interactivePressed: primary.textPressed,
            interactiveDisabled: disabled,
            focused: primary.border,
        },
        border: {
            ...semantic.border,
            default: border,
            subdued: borderSubdued,
            hovered: isDark ? '#ABC1C4' : RESQ_BRAND_COLORS.slate,
            primary: primary.border,
            primaryPressed: primary.borderPressed,
            primaryDisabled: primary.borderDisabled,
            secondary: secondary.border,
            secondaryPressed: secondary.borderPressed,
            secondaryDisabled: secondary.borderDisabled,
            interactive: primary.border,
            focused: primary.border,
            dark: isDark ? '#ABC1C4' : RESQ_BRAND_COLORS.slate,
        },
        divider: {
            default: borderSubdued,
            table: isDark ? borderSubdued : '#E6E9EC',
            subtle: isDark ? '#244349' : '#F9F9F9',
        },
    };
};

export interface ThemePalette {
    brand: typeof RESQ_BRAND_COLORS;
    toggle: {
        track: string;
        trackHovered: string;
        trackBorder: string;
        checkedTrack: string;
        checkedTrackHovered: string;
        thumb: string;
    };
    background: {
        default: string;
        navigation: string;
    };
    surface: {
        getBase(colorRole: ColorRole): string;
        getHovered(colorRole: ColorRole): string;
        getPressed(colorRole: ColorRole): string;
        getDisabled(colorRole: ColorRole): string;
        getSubduedHovered(colorRole: ColorRole): string | undefined;
        getSubduedPressed(colorRole: ColorRole): string | undefined;
        disabled: string;
        default: string;
        subdued: string;
        hovered: string;
        pressed: string;
        selected: string;
        primary: string;
        primaryHovered: string;
        primaryPressed: string;
        primaryDisabled: string;
        primarySubduedHovered: string;
        primarySubduedPressed: string;
        secondary: string;
        secondaryHovered: string;
        secondaryPressed: string;
        secondaryDisabled: string;
        critical: string;
        criticalHovered: string;
        criticalPressed: string;
        criticalDisabled: string;
        criticalSubdued: string;
        criticalSubduedHovered: string;
        criticalSubduedPressed: string;
        success: string;
        successSecondary: string;
        successHovered: string;
        successPressed: string;
        successDisabled: string;
        successSubdued: string;
        successSubduedHovered: string;
        successSubduedPressed: string;
        highlight: string;
        highlight2: string;
        highlightSubdued: string;
        warning: string;
        warningSecondary: string;
        warningSubdued: string;
        warningSubduedAlt: string;
        decorative1: string;
        decorative1Subdued: string;
        decorative2: string;
        decorative2Subdued: string;
        decorative3: string;
        decorative3Subdued: string;
        decorative4: string;
        decorative4Subdued: string;
        interactive: string;
        interactiveDisabled: string;
        tableSubdued: string;
        nora: string;
        noraDark: string;
        noraGradient: string;
        noraFooter: string;
        noraDivider: string;
        growthHubPremium: string;
    };
    text: {
        getBase(colorRole: ColorRole): string | undefined;
        getPressed(colorRole: ColorRole): string | undefined;
        getDisabled(colorRole: ColorRole): string | undefined;
        getOn(colorRole: ColorRole): string | undefined;
        default: string;
        subdued: string;
        disabled: string;
        onSecondary: string;
        primary: string;
        primaryPressed: string;
        primaryDisabled: string;
        onPrimary: string;
        critical: string;
        criticalPressed: string;
        criticalDisabled: string;
        onCritical: string;
        onCriticalSubdued: string;
        success: string;
        successSecondary: string;
        successPressed: string;
        successDisabled: string;
        onSuccess: string;
        onSuccessSubdued: string;
        highlight: string;
        onHighlight: string;
        warning: string;
        onWarning: string;
        onWarningSubdued: string;
        onWarningSubduedAlt: string;
        decorative1: string;
        onDecorative1Subdued: string;
        decorative2: string;
        onDecorative2Subdued: string;
        decorative3: string;
        decorative4: string;
        onDecorative4Subdued: string;
        interactive: string;
        interactiveHovered: string;
        interactivePressed: string;
        interactiveDisabled: string;
        placeHolder: string;
        link: string;
    };
    icon: {
        default: string;
        subdued: string;
        disabled: string;
        primary: string;
        onPrimary: string;
        tertiary: string;
        tertiaryDisabled: string;
        warning: string;
        onWarning: string;
        critical: string;
        criticalDisabled: string;
        onCritical: string;
        success: string;
        successDisabled: string;
        onSuccess: string;
        highlight: string;
        onHighlight: string;
        decorative1: string;
        decorative1Subdued: string;
        decorative2: string;
        decorative2Subdued: string;
        decorative3: string;
        decorative3Subdued: string;
        decorative4: string;
        decorative4Subdued: string;
        decorative4Bright: string;
        interactive: string;
        interactiveHovered: string;
        interactivePressed: string;
        interactiveDisabled: string;
        interactiveCritical: string;
        interactiveCriticalHovered: string;
        interactiveCriticalPressed: string;
        interactiveCriticalDisabled: string;
        focused: string;
    };
    border: {
        getBase(colorRole: ColorRole): string;
        getPressed(colorRole: ColorRole): string;
        getDisabled(colorRole: ColorRole): string;
        getSubdued(colorRole: ColorRole): string | undefined;
        default: string;
        subdued: string;
        hovered: string;
        primary: string;
        primaryPressed: string;
        primaryDisabled: string;
        onPrimary: string;
        secondary: string;
        secondaryPressed: string;
        secondaryDisabled: string;
        onSecondary: string;
        critical: string;
        criticalPressed: string;
        criticalDisabled: string;
        criticalSubdued: string;
        onCritical: string;
        success: string;
        successPressed: string;
        successDisabled: string;
        successSubdued: string;
        onSuccess: string;
        highlight: string;
        highlightSubdued: string;
        warning: string;
        warningSubdued: string;
        warningSubduedAlt: string;
        decorative1: string;
        decorative2: string;
        decorative3: string;
        decorative4: string;
        interactive: string;
        focused: string;
        dark: string;
    };
    divider: {
        default: string;
        table: string;
        subtle: string;
    };
}

export const createTheme: (options?: ThemeOptions, ..._args: Record<string, unknown>[]) => Theme = (
    options,
    ..._args
) => {
    const interfacePalette = getInterfacePalette('light');
    const optionsWithDefaults = merge(
        {
            // Keep MUI palette available, including custom keys we augment via typings
            palette: {},
            resqPalette: {
                brand: RESQ_BRAND_COLORS,
                toggle: interfacePalette.toggle,
                background: {
                    ...interfacePalette.background,
                },
                surface: {
                    ...interfacePalette.surface,
                    getBase(colorRole: ColorRole): string {
                        return (this as any)[colorRole] as string;
                    },
                    getHovered(colorRole: ColorRole): string {
                        return (this as any)[`${colorRole}Hovered`] as string;
                    },
                    getPressed(colorRole: ColorRole): string {
                        return (this as any)[`${colorRole}Pressed`] as string;
                    },
                    getDisabled(colorRole: ColorRole): string {
                        return (this as any)[`${colorRole}Disabled`] as string;
                    },
                    getSubduedHovered(colorRole: ColorRole): string | undefined {
                        return (this as any)[`${colorRole}SubduedHovered`];
                    },
                    getSubduedPressed(colorRole: ColorRole): string | undefined {
                        return (this as any)[`${colorRole}SubduedPressed`];
                    },
                    // Filled-button surfaces are tuned so white labels reach 4.5:1.
                    highlight: '#7B5DE6',
                    highlight2: RESQ_BRAND_COLORS.deepTeal,
                    highlightSubdued: '#F2EFFC',
                    decorative1: '#FF4801',
                    decorative1Subdued: '#FDEEE5',
                    decorative2: '#00A1A3',
                    decorative2Subdued: '#DBF6F6',
                    decorative3: '#7D60E6',
                    decorative3Subdued: '#F2EFFC',
                    decorative4: '#AB8D40',
                    decorative4Subdued: '#FCF0D0',
                    nora: '#8FA88B',
                    noraDark: '#2F3B34',
                    noraGradient:
                        'conic-gradient(from 124deg at 57.19% 134.3%, #6F935E 0deg, #485B40 360deg)',
                    noraFooter: '#2A3A49',
                    noraDivider: RESQ_BRAND_COLORS.deepTeal,
                    growthHubPremium: RESQ_BRAND_COLORS.deepTeal,
                },
                text: {
                    ...interfacePalette.text,
                    getBase(colorRole: ColorRole): string | undefined {
                        return (this as any)[colorRole];
                    },
                    getPressed(colorRole: ColorRole): string | undefined {
                        return (this as any)[`${colorRole}Pressed`];
                    },
                    getDisabled(colorRole: ColorRole): string | undefined {
                        return (this as any)[`${colorRole}Disabled`];
                    },
                    getOn(colorRole: ColorRole): string | undefined {
                        return (this as any)[`on${_.capitalize(colorRole)}`];
                    },
                    highlight: '#6248C5',
                    onHighlight: '#FFFFFF',
                    decorative1: '#994613',
                    decorative2: '#00696B',
                    decorative3: '#6248C5',
                    decorative4: '#705C29',
                },
                icon: {
                    ...interfacePalette.icon,
                    highlight: '#7D60E6',
                    onHighlight: '#FFFFFF',
                    decorative1: '#FF4801',
                    decorative1Subdued: '#F4935D',
                    decorative2: '#00A1A3',
                    decorative2Subdued: '#00BEC0',
                    decorative3: '#7D60E6',
                    decorative3Subdued: '#B1A0F0',
                    decorative4: '#AB8D40',
                    decorative4Subdued: '#CAA74B',
                    decorative4Bright: '#F6D06E',
                },
                border: {
                    ...interfacePalette.border,
                    getBase(colorRole: ColorRole): string {
                        return (this as any)[colorRole] as string;
                    },
                    getPressed(colorRole: ColorRole): string {
                        return (this as any)[`${colorRole}Pressed`] as string;
                    },
                    getDisabled(colorRole: ColorRole): string {
                        return (this as any)[`${colorRole}Disabled`] as string;
                    },
                    getSubdued(colorRole: ColorRole): string | undefined {
                        return (this as any)[`${colorRole}Subdued`];
                    },
                    onPrimary: 'rgba(15, 16, 26, 0.3)',
                    onSecondary: 'rgba(15, 16, 26, 0.3)',
                    highlight: '#7D60E6',
                    highlightSubdued: '#9780EB',
                    decorative1: '#FF4801',
                    decorative2: '#00A1A3',
                    decorative3: '#7D60E6',
                    decorative4: '#AB8D40',
                },
                divider: {
                    ...interfacePalette.divider,
                },
            },
        },
        options ?? {},
    );
    // Backfill custom palette entries used by MUI components (neutral, danger, payment)
    // so color="neutral"/"danger"/"payment" props keep working.
    const rp = (optionsWithDefaults as any).resqPalette as ThemePalette;
    const pal = (optionsWithDefaults as any).palette as any;
    pal.neutral = pal.neutral ?? {
        // a light neutral for backgrounds/borders
        main: rp.border.subdued ?? rp.border.default,
        contrastText: rp.text.default,
    };
    pal.danger = pal.danger ?? {
        main: rp.surface.critical,
        contrastText: rp.text.onCritical,
    };
    pal.payment = pal.payment ?? {
        main: rp.surface.successSecondary ?? rp.surface.success,
        contrastText: rp.text.onSuccess,
    };
    const baseTheme = createMuiTheme(optionsWithDefaults, ..._args);

    // Create custom shadows array (must maintain exact length of 25 elements)
    const customShadows = [...baseTheme.shadows] as typeof baseTheme.shadows;
    customShadows[1] = '0px 4px 8px 0px rgba(218, 218, 218, 0.25)';

    // Create final theme with all augmented properties
    const finalTheme = createMuiTheme(
        {
            ...baseTheme,
            shadows: customShadows,
        },
        ..._args,
    );

    return finalTheme;
};
